export interface GemEntry {
  name: string;
  gemLevel: number;
  gemQuality: number;
  corrupted: boolean;
  divineValue: number;
  chaosValue: number;
  count: number;
  variant?: string;
  icon?: string;
}

export interface GemResult {
  name: string;
  icon?: string;
  bestLevel: number;
  best: number;
  bestChaos: number;
  bestLevel_str: string;
  bestCount: number;
  mid: number;
  midChaos: number;
  midLevel_str: string;
  midReal: number | null;
  midCount: number;
  worst: number;
  worstChaos: number;
  worstLevel_str: string;
  worstReal: number | null;
  worstCount: number;
  cost: number;
  costChaos: number;
  costCount: number;
  ev: number;
  profit: number;
}

export const MIN_COUNT = 10;

const UNCAPPED_GEMS = new Set([
  "enhance support", "empower support", "enlighten support",
  "awakened enhance support", "awakened empower support", "awakened enlighten support",
]);

function isUncapped(name: string): boolean {
  return UNCAPPED_GEMS.has(name.trim().toLowerCase());
}

function cheapest(pool: GemEntry[]): GemEntry {
  return pool.reduce((a, b) => (a.divineValue <= b.divineValue ? a : b));
}
function mostQuality(pool: GemEntry[]): GemEntry {
  return pool.reduce((a, b) => (a.gemQuality >= b.gemQuality ? a : b));
}
function withMinCount(pool: GemEntry[], min: number): GemEntry[] {
  const filtered = pool.filter((e) => e.count >= min);
  return filtered.length > 0 ? filtered : pool;
}


export async function fetchAndCompute(minCount = MIN_COUNT, league = "Allflame"): Promise<GemResult[]> {
  const res = await fetch(`/api/gems?league=${encodeURIComponent(league)}`);
  const data = await res.json();
  const lines: any[] = data.lines;

  const byName = new Map<string, GemEntry[]>();
  for (const raw of lines) {
    const entry: GemEntry = {
      name: raw.name,
      gemLevel: raw.gemLevel ?? 1,
      gemQuality: raw.gemQuality ?? 0,
      corrupted: raw.corrupted ?? false,
      divineValue: raw.divineValue ?? 0,
      chaosValue: raw.chaosValue ?? 0,
      count: raw.count ?? 0,
      variant: raw.variant,
      icon: raw.icon,
    };
    if (!byName.has(entry.name)) byName.set(entry.name, []);
    byName.get(entry.name)!.push(entry);
  }

  const results: GemResult[] = [];

  for (const [name, entries] of byName) {
    const corrupted    = entries.filter((e) => e.corrupted);
    const notCorrupted = entries.filter((e) => !e.corrupted);

    if (corrupted.length === 0) continue;

    const uncapped = isUncapped(name);
    const icon = entries.find((e) => e.icon)?.icon;

    // ── BEST ────────────────────────────────────────────────────────────────
    let bestLevel: number;
    let bestEntry: GemEntry;

    if (uncapped) {
      const validCorrupted = withMinCount(corrupted, minCount);
      bestLevel = Math.max(...validCorrupted.map((e) => e.gemLevel));
      const pool = withMinCount(corrupted.filter((e) => e.gemLevel === bestLevel), minCount);
      bestEntry = cheapest(pool);
    } else {
      const capped = corrupted.filter((e) => e.gemQuality <= 20);
      if (capped.length === 0) continue;
      const validCapped = withMinCount(capped, minCount);
      bestLevel = Math.max(...validCapped.map((e) => e.gemLevel));
      const pool = withMinCount(capped.filter((e) => e.gemLevel === bestLevel), minCount);
      bestEntry = mostQuality(pool);
    }
    const bestVal = bestEntry.divineValue;
    const bestLevel_str = `${bestLevel}`;

    // ── MID ─────────────────────────────────────────────────────────────────
    // uncapped: cheapest at bestLevel-1
    // normal:   bestLevel-1, quality >= 20, highest quality
    const midLevel = bestLevel - 1;
    let midEntry: GemEntry | null = null;
    let midLevel_str: string;

    if (uncapped) {
      const pool = withMinCount(corrupted.filter((e) => e.gemLevel === midLevel), minCount);
      if (pool.length > 0) {
        midEntry = cheapest(pool);
        midLevel_str = `${midLevel}`;
      } else {
        const below = corrupted.filter((e) => e.gemLevel < bestLevel);
        if (below.length > 0) {
          midEntry = cheapest(below);
          midLevel_str = `${midEntry.gemLevel}*`;
        } else { midLevel_str = `${midLevel}*`; }
      }
    } else {
      // mid: quality >= 20
      const pool = withMinCount(
        corrupted.filter((e) => e.gemLevel === midLevel && e.gemQuality >= 20), minCount
      );
      if (pool.length > 0) {
        midEntry = mostQuality(pool);
        midLevel_str = `${midLevel}`;
      } else {
        const below = corrupted.filter((e) => e.gemLevel < bestLevel && e.gemQuality >= 20);
        if (below.length > 0) {
          midEntry = cheapest(below);
          midLevel_str = `${midEntry.gemLevel}*`;
        } else { midLevel_str = `${midLevel}*`; }
      }
    }
    const midVal   = midEntry?.divineValue ?? null;
    const midCount = midEntry?.count ?? 0;

    // ── WORST ───────────────────────────────────────────────────────────────
    // uncapped: cheapest corrupted below best
    // normal:   bestLevel-1, quality < 20, cheapest
    let worstEntry: GemEntry | null = null;
    let worstLevel_str: string;

    if (uncapped) {
      const worstLevel = bestLevel - 2;
      const pool = withMinCount(corrupted.filter((e) => e.gemLevel === worstLevel), minCount);
      if (pool.length > 0) {
        worstEntry = cheapest(pool);
        worstLevel_str = `${worstLevel}`;
      } else {
        const below = corrupted.filter((e) => e.gemLevel < bestLevel);
        if (below.length > 0) {
          worstEntry = cheapest(below);
          worstLevel_str = `${worstEntry.gemLevel}*`;
        } else {
          worstEntry = bestEntry;
          worstLevel_str = `${bestLevel}*`;
        }
      }
    } else {
      // worst: same level as mid (bestLevel-1), quality < 20, cheapest
      const pool = withMinCount(
        corrupted.filter((e) => e.gemLevel === midLevel && e.gemQuality < 20), minCount
      );
      if (pool.length > 0) {
        worstEntry = cheapest(pool);
        worstLevel_str = `${midLevel}`;
      } else {
        const below = corrupted.filter((e) => e.gemLevel < bestLevel && e.gemQuality < 20);
        if (below.length > 0) {
          worstEntry = cheapest(below);
          worstLevel_str = `${worstEntry.gemLevel}*`;
        } else {
          // no low-quality entries — fallback to any corrupted below best
          const below2 = corrupted.filter((e) => e.gemLevel < bestLevel);
          if (below2.length > 0) {
            worstEntry = cheapest(below2);
            worstLevel_str = `${worstEntry.gemLevel}*`;
          } else {
            worstEntry = bestEntry;
            worstLevel_str = `${bestLevel}*`;
          }
        }
      }
    }
    const worstVal   = worstEntry?.divineValue ?? null;
    const worstCount = worstEntry?.count ?? 0;

    // ── COST ────────────────────────────────────────────────────────────────
    const lvl1 = notCorrupted.filter((e) => e.gemLevel === 1);
    const costPool = withMinCount(lvl1.length > 0 ? lvl1 : notCorrupted, minCount);
    if (costPool.length === 0) continue;
    const costEntry = cheapest(costPool);
    const costVal   = costEntry.divineValue;
    const costCount = costEntry.count;

    // ── EV & PROFIT ─────────────────────────────────────────────────────────
    let effectiveMid   = midVal   ?? worstVal!;
    let effectiveWorst = worstVal ?? midVal!;

    effectiveMid   = Math.min(effectiveMid,   bestVal);
    effectiveWorst = Math.min(effectiveWorst, effectiveMid);

    // cost must be >= worst (you can't buy cheaper than the worst outcome)
    const effectiveCost = Math.max(costVal, effectiveWorst);

    const ev     = 0.25 * bestVal + 0.5 * effectiveMid + 0.25 * effectiveWorst;
    const profit = ev - effectiveCost;

    results.push({
      name, icon, bestLevel,
      best: bestVal,
      bestChaos: bestEntry.chaosValue,
      bestLevel_str,
      bestCount: bestEntry.count,
      mid: effectiveMid,
      midChaos: midEntry ? midEntry.chaosValue : (worstEntry?.chaosValue ?? 0),
      midLevel_str,
      midReal: midVal,
      midCount,
      worst: effectiveWorst,
      worstChaos: worstEntry ? worstEntry.chaosValue : (midEntry?.chaosValue ?? 0),
      worstLevel_str,
      worstReal: worstVal,
      worstCount,
      cost: effectiveCost,
      costChaos: costEntry.chaosValue,
      costCount,
      ev, profit,
    });
  }

  results.sort((a, b) => b.profit - a.profit);
  return results;
}
